import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDriverService from '../../services/useDriverService';
import { Driver } from '../../models/DriverModel';
import showToast from '../../utils/toast';
import DriversTable, { DriversViewMode } from './DriversTable';
import DriverForm from '../../components/Drivers/DriverForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import {
  canAccessDriverEditPage,
  canEditOrDeleteDrivers,
  canOpenDriverFormModal,
} from '../../hooks/usePermissions';
import { Plus, UserCog } from 'lucide-react';
import { driverHasUpcomingService } from './components/DriverCard';
import ReleaseDriverVehicleModal from './components/ReleaseDriverVehicleModal';
import './styles/DriversPage.css';

type SortOrder = 'asc' | 'desc';
type DriverSortField = 'first_name' | 'last_name';

const DRIVERS_VIEW_STORAGE_KEY = 'drivers_list_view_mode';

function readStoredViewMode(): DriversViewMode {
  try {
    const stored = sessionStorage.getItem(DRIVERS_VIEW_STORAGE_KEY);
    if (stored === 'table' || stored === 'cards') return stored;
  } catch {
    /* ignore */
  }
  return 'cards';
}

const DriversPage: React.FC = () => {
  const navigate = useNavigate();
  const svc = useDriverService();
  const { list, create, edit, store, update, remove, releaseVehicle, fetching } = svc;
  const [viewMode, setViewMode] = useState<DriversViewMode>(readStoredViewMode);

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [passes, setPasses] = useState<{ value: string; label: string }[]>([]);
  const [shifts, setShifts] = useState<{ value: string; label: string }[]>([]);
  const [shiftGroups, setShiftGroups] = useState<{ value: string; label: string }[]>([]);
  const [vehicles, setVehicles] = useState<{ value: string; label: string }[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Driver | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [toDelete, setToDelete] = useState<number | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<Driver | null>(null);
  const [releaseNeedsComment, setReleaseNeedsComment] = useState(false);
  const [releaseLoadingIds, setReleaseLoadingIds] = useState<number[]>([]);
  const releaseLoadingIdsRef = useRef<number[]>([]);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState('');
  const nameFilterRef = useRef(nameFilter);
  nameFilterRef.current = nameFilter;
  const [lastNameFilter, setLastNameFilter] = useState('');
  const lastNameFilterRef = useRef(lastNameFilter);
  lastNameFilterRef.current = lastNameFilter;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filterPatente, setFilterPatente] = useState('');
  const filterPatenteRef = useRef(filterPatente);
  filterPatenteRef.current = filterPatente;
  const patenteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const sortOrderRef = useRef<SortOrder>('asc');
  const [sortField, setSortField] = useState<DriverSortField>('first_name');
  const sortFieldRef = useRef<DriverSortField>('first_name');
  const [sortAnimation, setSortAnimation] = useState({ key: 0, target: '' });
  const canManageDrivers = canEditOrDeleteDrivers();
  const canOpenEditPage = canAccessDriverEditPage();
  const canEditDriverForm = canOpenDriverFormModal();
  const showDriverActions = canManageDrivers || canEditDriverForm;

  useEffect(() => {
    releaseLoadingIdsRef.current = releaseLoadingIds;
  }, [releaseLoadingIds]);

  const load = useCallback((page: number = 1) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const name = nameFilterRef.current.trim();
    if (name) filters.first_name = name;
    const lastName = lastNameFilterRef.current.trim();
    if (lastName) filters.last_name = lastName;
    const patente = filterPatenteRef.current.trim();
    if (patente) filters.license_plate = patente;
    filters.sort_order = sortOrderRef.current;
    filters.sort_field = sortFieldRef.current;

    list(filters as Record<string, string | number>, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        const items = Array.isArray(data.drivers) ? data.drivers : data.items ?? [];
        setDrivers(items);

        if (data.pagination) {
          setPagination({
            page: data.pagination.page || page,
            totalPages: data.pagination.total_pages || 1,
            totalCount: data.pagination.total_count || 0,
            pageSize: data.pagination.page_size || 10,
            hasNext: data.pagination.has_next || false,
            hasPrevious: data.pagination.has_previous || false,
          });
        }
      },
      onError: () => showToast('Error cargando conductores', 'error'),
    }, ctrl.signal);
  }, [list]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (patenteTimeoutRef.current) clearTimeout(patenteTimeoutRef.current);
    };
  }, [load]);

  const handleNameChange = useCallback(
    (value: string) => {
      nameFilterRef.current = value;
      setNameFilter(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1), 400);
    },
    [load]
  );

  const handleLastNameChange = useCallback(
    (value: string) => {
      lastNameFilterRef.current = value;
      setLastNameFilter(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1), 400);
    },
    [load]
  );

  const handlePatenteChange = useCallback(
    (value: string) => {
      filterPatenteRef.current = value;
      setFilterPatente(value);
      if (patenteTimeoutRef.current) clearTimeout(patenteTimeoutRef.current);
      patenteTimeoutRef.current = setTimeout(() => {
        load(1);
      }, 400);
    },
    [load]
  );

  const clearFilters = useCallback(() => {
    nameFilterRef.current = '';
    lastNameFilterRef.current = '';
    filterPatenteRef.current = '';
    setNameFilter('');
    setLastNameFilter('');
    setFilterPatente('');
    load(1);
  }, [load]);

  const handleSortToggle = useCallback((field: DriverSortField) => {
    const nextOrder: SortOrder =
      sortFieldRef.current === field && sortOrderRef.current === 'asc' ? 'desc' : 'asc';
    sortFieldRef.current = field;
    sortOrderRef.current = nextOrder;
    setSortField(field);
    setSortOrder(nextOrder);
    setSortAnimation((current) => ({ key: current.key + 1, target: field }));
    load(1);
  }, [load]);

  const handleViewModeChange = useCallback((mode: DriversViewMode) => {
    setViewMode(mode);
    try {
      sessionStorage.setItem(DRIVERS_VIEW_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const openCreate = () => {
    create({
      onSuccess: (resp: any) => {
        const data = resp.data ?? {};
        setPasses(data.passes?.map((p: any) => ({ value: String(p.id), label: p.name })) ?? []);
        setShifts(data.shifts?.map((s: any) => ({ value: String(s.id), label: s.name })) ?? []);
        setShiftGroups(data.shift_groups?.map((g: any) => ({ value: String(g.id), label: g.name })) ?? []);
        setVehicles(data.vehicles?.map((s: any) => ({ value: String(s.id), label: s.name })) ?? []);
        setEditing({
          first_name: '',
          last_name: '',
          address: '',
          vehicle: '',
          phone: '',
          status: 'Operativo',
          passes: [],
          shift: null,
          shift_groups: [],
        });
        setShowForm(true);
      },
      onError: () => showToast('Error cargando formulario', 'error'),
    });
  };

  const openEditPage = (d: Driver) => {
    if (!d.id) return;
    navigate(`/drivers/edit/${d.id}`);
  };

  const openEditFormModal = (d: Driver) => {
    if (!d.id) return;
    edit(Number(d.id), {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        const driverData = data.driver ?? {};
        setPasses(
          (data.passes ?? []).map((p: { id: number; name: string }) => ({
            value: String(p.id),
            label: p.name,
          }))
        );
        setShifts(
          (data.shifts ?? []).map((s: { id: number; name: string }) => ({
            value: String(s.id),
            label: s.name,
          }))
        );
        setShiftGroups(
          (data.shift_groups ?? []).map((g: { id: number; name: string }) => ({
            value: String(g.id),
            label: g.name,
          }))
        );
        setVehicles(
          (data.vehicles ?? []).map((v: { id: number; name: string }) => ({
            value: String(v.id),
            label: v.name,
          }))
        );
        const vehicleId = driverData.vehicle_id ?? driverData.vehicle;
        setEditing({
          ...driverData,
          id: driverData.id ?? d.id,
          vehicle: vehicleId ? String(vehicleId) : undefined,
          shift: driverData.shift ?? null,
          passes: Array.isArray(driverData.passes) ? driverData.passes : [],
          shift_groups: Array.isArray(driverData.shift_groups) ? driverData.shift_groups : [],
          status: driverData.status ?? 'Operativo',
        });
        clearErrors();
        setShowForm(true);
      },
      onError: () => showToast('Error cargando datos del conductor', 'error'),
    });
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;
    setSavingLocked(true);
    try {
      if (editing.id) {
        const r = await update(editing.id, {
          first_name: editing.first_name,
          last_name: editing.last_name,
          address: editing.address,
          passes: editing.passes,
          vehicle: editing.vehicle ? String(editing.vehicle) : undefined,
          phone: editing.phone,
          shift: editing.shift ? Number(editing.shift) : null,
          shift_groups: editing.shift_groups,
          status: editing.status,
          is_active: editing.is_active,
        });
        return r;
      } else {
        const r = await store({
          first_name: editing.first_name,
          last_name: editing.last_name,
          address: editing.address,
          passes: editing.passes,
          vehicle: editing.vehicle ? String(editing.vehicle) : undefined,
          phone: editing.phone,
          shift: editing.shift ? Number(editing.shift) : null,
          shift_groups: editing.shift_groups,
          status: editing.status,
          is_active: editing.is_active,
        });
        return r;
      }
    } catch (error: any) {
      const fields = error?.response?.data;

      if (fields?.errors) {
        setErrorsFromResponse(fields.errors);
        return;
      }

      setErrorsFromResponse(fields);
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(pagination.page);
    } else {
      setErrorsFromResponse(resp);
      showToast(resp?.message ?? 'Error guardando', 'error');
    }
  };

  const openDeleteConfirmation = (id: number) => {
    setToDelete(id);
    setIsAlertOpen(true);
  };

  const getReleaseErrorMessage = (payload: any): string => {
    if (!payload) return 'No se pudo liberar el vehículo.';
    const errors = payload?.errors;
    if (typeof errors === 'string' && errors.trim()) return errors.trim();
    if (Array.isArray(errors) && errors.length > 0) return String(errors[0]);
    if (errors && typeof errors === 'object') {
      const firstValue = Object.values(errors)[0];
      if (Array.isArray(firstValue) && firstValue.length > 0) return String(firstValue[0]);
      if (firstValue != null) return String(firstValue);
    }
    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }
    return 'No se pudo liberar el vehículo.';
  };

  const executeReleaseVehicle = useCallback(
    async (driver: Driver, comment?: string) => {
      if (!driver.id) return;
      const driverId = Number(driver.id);
      if (releaseLoadingIdsRef.current.includes(driverId)) return;

      setReleaseLoadingIds((prev) => [...prev, driverId]);
      try {
        const resp: any = await releaseVehicle(driverId, comment);
        const failed =
          !resp ||
          resp?.success === false ||
          (typeof resp?.http_status === 'number' && resp.http_status >= 400) ||
          (resp?.errors && Object.keys(resp.errors).length > 0);

        if (failed) {
          showToast(getReleaseErrorMessage(resp), 'error');
          return;
        }

        showToast(resp?.message || 'Vehículo liberado correctamente', 'success');
        setReleaseTarget(null);
        setReleaseNeedsComment(false);
        load(pagination.page);
      } catch (error: any) {
        const errorData = error?.response?.data;
        showToast(getReleaseErrorMessage(errorData ?? error), 'error');
      } finally {
        setReleaseLoadingIds((prev) => prev.filter((id) => id !== driverId));
      }
    },
    [releaseVehicle, load, pagination.page]
  );

  const handleRequestReleaseVehicle = useCallback((driver: Driver) => {
    if (!driver.id || !driver.vehicle_license_plate?.trim()) return;
    setReleaseTarget(driver);
    setReleaseNeedsComment(driverHasUpcomingService(driver));
  }, []);

  const handleConfirmReleaseWithComment = useCallback(
    async (comment: string) => {
      if (!releaseTarget) return;
      await executeReleaseVehicle(releaseTarget, comment);
    },
    [releaseTarget, executeReleaseVehicle]
  );

  const handleConfirmReleaseSimple = useCallback(() => {
    if (!releaseTarget) return;
    void executeReleaseVehicle(releaseTarget);
  }, [releaseTarget, executeReleaseVehicle]);

  const handleConfirmDelete = () => {
    if (toDelete === null) return;
    remove(toDelete, {
      onSuccess: () => {
        showToast('Eliminado', 'success');
        load(pagination.page);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });
    setIsAlertOpen(false);
    setToDelete(null);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const activeFilterLabel = [nameFilter, lastNameFilter, filterPatente]
    .map((v) => v.trim())
    .filter(Boolean)
    .join(' · ');

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || drivers.length}
        </span>
        conductores
      </span>
    );

  return (
    <MaintainerPageShell
      title="Conductores"
      icon={<UserCog size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo conductor
        </button>
      }
    >
      <DriversTable
        data={drivers}
        loading={fetching.list}
        onEditPage={canOpenEditPage ? openEditPage : undefined}
        onEditForm={canEditDriverForm ? openEditFormModal : undefined}
        onDelete={canManageDrivers ? openDeleteConfirmation : undefined}
        onReleaseVehicle={canManageDrivers ? handleRequestReleaseVehicle : undefined}
        releaseVehicleLoadingIds={releaseLoadingIds}
        showActions={showDriverActions}
        totalCount={pagination.totalCount}
        searchTerm={activeFilterLabel}
        onClearSearch={clearFilters}
        sortOrder={sortOrder}
        sortField={sortField}
        sortAnimationKey={sortAnimation.key}
        sortAnimationTarget={sortAnimation.target}
        onSortToggle={handleSortToggle}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        toolbar={
          <div className="drivers-filters">
            <div className="drivers-filters__field">
              <label htmlFor="drivers-filter-name">Nombre</label>
              <input
                id="drivers-filter-name"
                type="search"
                value={nameFilter}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ej. Juan"
                autoComplete="off"
              />
            </div>
            <div className="drivers-filters__field">
              <label htmlFor="drivers-filter-last-name">Apellido</label>
              <input
                id="drivers-filter-last-name"
                type="search"
                value={lastNameFilter}
                onChange={(e) => handleLastNameChange(e.target.value)}
                placeholder="Ej. Pérez"
                autoComplete="off"
              />
            </div>
            <div className="drivers-filters__field">
              <label htmlFor="drivers-filter-patente">Patente</label>
              <input
                id="drivers-filter-patente"
                type="search"
                value={filterPatente}
                onChange={(e) => handlePatenteChange(e.target.value)}
                placeholder="Ej. ABCD12"
                autoComplete="off"
              />
            </div>
          </div>
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="drivers-modal">
          <div className="drivers-modal-inner">
            <h3>{editing.id ? 'Editar conductor' : 'Nuevo conductor'}</h3>
            <DriverForm
              value={editing}
              onChange={setEditing}
              error={fieldError || undefined}
              passes={passes}
              shifts={shifts}
              shiftGroups={shiftGroups}
              vehicles={vehicles}
              fieldErrors={fieldErrors}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar conductor?"
        message="Esta acción no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setIsAlertOpen(false);
          setToDelete(null);
        }}
      />

      <ConfirmAlert
        isOpen={Boolean(releaseTarget) && !releaseNeedsComment}
        title="¿Desasignar vehículo?"
        message={
          releaseTarget?.vehicle_license_plate
            ? `Se desasignará el vehículo ${releaseTarget.vehicle_license_plate} de ${releaseTarget.first_name} ${releaseTarget.last_name}. Quedará disponible para otro conductor.`
            : 'Se desasignará el vehículo del conductor. Quedará disponible para reasignación.'
        }
        onConfirm={handleConfirmReleaseSimple}
        onClose={() => {
          setReleaseTarget(null);
          setReleaseNeedsComment(false);
        }}
      />

      <ReleaseDriverVehicleModal
        isOpen={Boolean(releaseTarget) && releaseNeedsComment}
        driverName={
          releaseTarget
            ? `${releaseTarget.first_name ?? ''} ${releaseTarget.last_name ?? ''}`.trim()
            : null
        }
        vehiclePlate={releaseTarget?.vehicle_license_plate}
        loading={
          Boolean(
            releaseTarget?.id &&
              releaseLoadingIds.includes(Number(releaseTarget.id))
          )
        }
        onConfirm={handleConfirmReleaseWithComment}
        onClose={() => {
          if (
            releaseTarget?.id &&
            releaseLoadingIds.includes(Number(releaseTarget.id))
          ) {
            return;
          }
          setReleaseTarget(null);
          setReleaseNeedsComment(false);
        }}
      />
    </MaintainerPageShell>
  );
};

export default DriversPage;
